import React, { FC, useCallback, useState } from 'react';
import { T, color3ToHex, hexToRgb01 } from 'src/shared/helpers';
import {
    PropertiesPanelAccordion,
    PropertiesPanelColorProperty,
    PropertiesPanelRangeProperty,
    PropertiesPanelSelectProperty,
    PropertiesPanelSwitch,
    PropertiesPanelSwitchProperty
} from 'src/components/pages/productConfiguration/steps/configurator-components/scene-properties-panel';
import InspectorShell from './inspectorShell';
import { ColorCorrectionIcon, PostProcessIcon } from '../../../../../widgets/icons/scene';
import { SHOW_NOT_YET_AVAILABLE } from './notYetAvailable';

// EFFECTS — cosa succede all'immagine DOPO che la scena è stata disegnata.
//
// Di tutto il pannello del prototipo, il backoffice sa fare la prima metà, e il motore ha i suoi
// setter per tutta. Il tone mapping è **della camera** (`SceneCameraConfig`: `toneMappingEnabled`,
// `toneMappingType`); esposizione e contrasto sono **della scena** (`SceneGeneralConfig`:
// `exposure`, `contrast`), perché descrivono l'immagine finita e non il punto da cui la si guarda.
// Il modello ha entrambe le coppie, e i due campi della camera sono dove finivano prima.
//
// DEI SEI FILTRI, UNO C'È DAVVERO ed è il GLOW: `SceneGlowConfig` nel modello, `glow` nel
// salvataggio, e sei metodi sul motore. Era in elenco fra i «non disponibili» sotto il nome
// «Bloom», che è anche il motivo per cui non si vedeva: sono due cose vicine ma diverse — un
// bloom fa sbordare qualunque cosa sia luminosa, il GlowLayer di Babylon fa brillare ciò che è
// EMISSIVO. Chiamarlo col suo nome è la differenza fra una funzione che c'è e una che manca.
//
// TINTA, SATURAZIONE E LUMINOSITÀ SONO COMPLETE — motore, modello e server. Sono le `colorCurves`
// di Babylon; i quattro campi stanno su `SceneGeneralConfig` (nullable), i setter in 3D core
// accanto a `setSceneContrast`, e chi carica la scena li rimette — anche in vetrina. Il dettaglio
// sta sui `changeTint` / `changeColorSaturation` / `changeBrightness`.
//
// E LA VIGNETTATURA È IL SECONDO, ma non è mai stata della stessa famiglia: gli altri quattro
// vogliono un passaggio di post-process, lei è tre campi della `imageProcessingConfiguration` —
// stessa casa di color grading, esposizione e tone mapping. Costo per fotogramma zero. Sta fra i
// filtri perché è lì che la cerca chi la usa, non perché sia fatta come loro.
//
// Gli altri quattro filtri **non esistono**: né campo nel config, né metodo sul motore. Le righe
// sono scritte e spente, e si disegnano solo con `SHOW_NOT_YET_AVAILABLE` acceso: chi aggiunge il
// motore trova il posto già pronto invece di doverlo inventare. E prima di aggiungerlo dovrà
// sciogliere un nodo: `disableAntialiasing()` fa `postProcessesEnabled = false`, che è un
// interruttore di TUTTA la scena — con l'antialiasing spento su mobile sparirebbero anche loro.
//
// `toneMappingType` non ha un setter sul motore, ma il motore lo legge da un posto solo: la
// `imageProcessingConfiguration` della scena. Ci scriviamo noi — vedi `changeToneMapping`.
//
// L'ANTIALIASING NON STA PIÙ QUI: è tornato in Settings > Rendering (prototipo V3; utente, 2026-10-19).

// LA TESTATA E I CAPITOLI NON SI DISEGNANO PIÙ QUI: erano gli stessi di `worldEditor`, riscritti
// una seconda volta, e a forza di riscriverli le due copie avevano smesso di assomigliarsi.

// Babylon: ImageProcessingConfiguration.TONEMAPPING_STANDARD = 0, TONEMAPPING_ACES = 1.
const TONE_MAPPING_OPTIONS = [
    { value: -1, label: T._('None', 'Admin') },
    { value: 0, label: T._('Standard', 'Admin') },
    { value: 1, label: T._('ACES Filmic', 'Admin') }
];

// LA VIGNETTATURA HA LASCIATO QUESTO ELENCO, e non perché l'abbiamo scritta: perché non era mai
// stata dei loro. Gli altri quattro vogliono un passaggio di post-process; lei è un campo della
// `imageProcessingConfiguration`, come il color grading. Era in questa lista per come appare a
// schermo, non per com'è fatta.
const FILTERS = ['Ambient occlusion', 'Depth of field', 'Film grain', 'Chromatic aberration'];

// I NEUTRI DELLA VIGNETTATURA SONO QUELLI DI BABYLON, non nostri: accendendola senza aver scelto
// niente il motore fa una vignettatura nera di peso 1.5, e il pannello deve mostrare quella —
// due numeri inventati qui direbbero una cosa e se ne vedrebbe un'altra.
const VIGNETTE_DEFAULT_WEIGHT = 1.5;
const VIGNETTE_DEFAULT_COLOR = '#000000';

// IL NEUTRO DELLA TINTA È UN COLORE SENZA SATURAZIONE, e il bianco è quello che si legge come
// «nessun filtro». Qualunque grigio andrebbe uguale — è la saturazione a zero che spegne il filtro,
// non la tinta — ma un campione bianco lo dice e un grigio medio sembra una scelta.
const NEUTRAL_TINT = '#ffffff';

// LA STRADA DI RITORNO: due numeri salvati tornano a essere il colore da mostrare nella pastiglia.
// La conversione sta qui e non nel motore perché il motore non ha una pastiglia da disegnare — lui
// tiene i quattro numeri che lo shader consuma, e il colore è il modo in cui due di quelli si
// chiedono a una persona.
//
// DENSITÀ ZERO È «NESSUN FILTRO», e si mostra bianco: qualunque grigio direbbe la stessa cosa — è la
// saturazione a spegnere il filtro, non la tinta — ma un campione bianco lo dice e un grigio medio
// sembra una scelta.
const hueDensityToHex = (hue?: number | null, density?: number | null): string => {
    if (hue == null || density == null || density <= 0) return NEUTRAL_TINT;
    return color3ToHex(BABYLON.Color3.FromHSV(hue, Math.min(density / 100, 1), 1));
};

interface EffectsEditorProps {
    sceneEditor?: any;
    zkScene?: any;
    /**
     * QUALE DELLE DUE VISTE DI LOOK (prototipo D150): Color correction — tone mapping e color grading — o
     * Post-process, i filtri che si accendono sull'immagine. Lo stato è uno, i pannelli due.
     */
    view: 'color' | 'postprocess';
}

const EffectsEditor: FC<EffectsEditorProps> = ({ sceneEditor, zkScene, view }) => {
    const cameraConfig = zkScene?.getOrCreateCameraConfig?.();
    const generalConfig = zkScene?.getOrCreateGeneralConfig?.();
    // IL CONFIG DEL GLOW SI LEGGE SENZA CREARLO. Con `getOrCreateGlowConfig` bastava APRIRE questo
    // pannello per lasciare un `glow: {}` nel modello, e `SceneGlowConfigInput.enabled` è
    // `Boolean!`: quel blocco vuoto fa fallire il salvataggio dell'INTERA scena, non del glow. Si
    // crea quando si scrive — vedi `writeGlowConfig`, che lo fa nascere già completo.
    const glowConfig = zkScene?.getGlowConfig?.();

    const initialMode = (cameraConfig?.get?.('toneMappingEnabled') ?? false) ? (cameraConfig?.get?.('toneMappingType') ?? 1) : -1;
    const [toneMappingMode, setToneMappingMode] = useState<number>(initialMode);

    // ESPOSIZIONE E CONTRASTO SI LEGGONO DOVE IL MOTORE LI APPLICA: la configurazione generale della
    // scena, e se manca il neutro (QA, 2026-10-12). Il ripiego sulla camera mostrava un valore che nessuno
    // disegna — il motore la legge solo in un percorso che non chiama più nessuno (`update_OLD`) — e le
    // scene del backoffice di prima mostravano 0,8 e 1,6 mentre il palco usava 1 e 1. Il dato della camera
    // resta dov'è: non si tocca, si smette solo di mostrarlo come se valesse.
    const [exposure, setExposure] = useState<number>(generalConfig?.get?.('exposure') ?? 1);
    const [contrast, setContrast] = useState<number>(generalConfig?.get?.('contrast') ?? 1);

    // IL CONFIG PRIMA DEL MOTORE: è il config che il salvataggio manda al server, quindi è lui a
    // dire com'è fatta questa scena. Il motore risponde per la scena che sta disegnando ADESSO, e
    // serve come ripiego quando il config non porta ancora niente.
    const [glow, setGlow] = useState<boolean>(glowConfig?.get?.('enabled') ?? sceneEditor?.getGlowEnabled?.() ?? false);
    const [glowIntensity, setGlowIntensity] = useState<number>(glowConfig?.get?.('intensity') ?? sceneEditor?.getGlowIntensity?.() ?? 1);
    const [glowBlur, setGlowBlur] = useState<number>(glowConfig?.get?.('blurSize') ?? sceneEditor?.getGlowBlurSize?.() ?? 16);

    // DUE SETTER DEL MOTORE, E NESSUNA MANO DENTRO BABYLON. Il tipo di tone mapping non passa per
    // le opzioni dell'EnvironmentHelper come i suoi fratelli, quindi per un po' l'ha scritto questo
    // pannello, mettendo le mani nell'`imageProcessingConfiguration` della scena: funzionava, ma
    // toccare il motore da fuori è il modo in cui due punti della stessa verità cominciano a
    // divergere. Adesso quel gesto è `setViewerCameraToneMappingType`, e sta dove stanno gli altri.
    //
    // LA STRADA DEL FRONTEND QUI NON VA BENE. In visualizzazione si chiama
    // `sceneViewer.updateFromConfig(config, { preventRefresh: false })`, che rifà il setup INTERO
    // della scena: luci, ombre, camera, info point, ambiente, e un SceneHelper nuovo. Là non costa
    // niente perché non si sta modificando niente; qui vorrebbe dire ricostruire la scena sotto le
    // mani di chi la sta editando, ogni volta che si cambia una tendina.
    const changeToneMapping = useCallback(
        (mode: number) => {
            setToneMappingMode(mode);
            sceneEditor?.setViewerCameraToneMappingEnabled?.(mode >= 0);
            if (mode >= 0) sceneEditor?.setViewerCameraToneMappingType?.(mode);
        },
        [sceneEditor]
    );

    const changeExposure = useCallback(
        (value: number) => {
            setExposure(value);
            sceneEditor?.setSceneExposure?.(value);
        },
        [sceneEditor]
    );

    const changeContrast = useCallback(
        (value: number) => {
            setContrast(value);
            sceneEditor?.setSceneContrast?.(value);
        },
        [sceneEditor]
    );

    // TINTA, SATURAZIONE E LUMINOSITÀ — le `colorCurves` di Babylon, che vivono nella stessa
    // `imageProcessingConfiguration` di tone mapping, esposizione e contrasto. Non è una pipeline:
    // nessun passaggio in più, nessun render target, costo per fotogramma zero.
    //
    // I PARAMETRI SONO QUATTRO E LE RIGHE TRE, ed è il motivo per cui una riga «Hue» da sola non
    // farebbe niente:
    //     colorCurvesHue         quale tinta                 0…360        ┐ insieme sono un COLORE,
    //     colorCurvesDensity     quanto è satura la tinta    0…100        ┘ e si scelgono guardandolo
    //     colorCurvesSaturation  desatura L'IMMAGINE INTERA  -100…100     → la riga Saturation
    //     colorCurvesBrightness  quanto è chiara l'immagine  -100…100     → la riga Brightness
    //
    // ADESSO SI SALVANO: i quattro campi stanno su `SceneGeneralConfigInput` (nullable, quindi una
    // scena vecchia che non li porta resta valida), i setter stanno in 3D core accanto a
    // `setSceneContrast`, e il motore li rimette al caricamento in `SceneHelper.setUpSceneColorCurves`
    // — anche in vetrina. Questo pannello non tocca più Babylon da fuori: chiama i setter, come
    // fanno esposizione e contrasto qui sopra.
    const [tint, setTint] = useState<string>(() => hueDensityToHex(generalConfig?.get?.('colorCurvesHue'), generalConfig?.get?.('colorCurvesDensity')));
    const [colorSaturation, setColorSaturation] = useState<number>(generalConfig?.get?.('colorCurvesSaturation') ?? 0);
    const [brightness, setBrightness] = useState<number>(generalConfig?.get?.('colorCurvesBrightness') ?? 0);

    const changeTint = useCallback(
        (hex: string) => {
            const rgb = hexToRgb01(hex);
            // `toHSV` restituisce un Color3 che porta tinta in r (0-360), saturazione in g e valore in b.
            const hsv = new BABYLON.Color3(rgb.r, rgb.g, rgb.b).toHSV();

            // LA PASTIGLIA MOSTRA QUELLO CHE VIENE APPLICATO DAVVERO. Del colore scelto il motore
            // usa tinta e saturazione; la luminosità è la riga Brightness, che è un'altra cosa. Un
            // rosso scuro e un rosso acceso darebbero lo stesso filtro, quindi il campione si
            // riporta a piena luminosità invece di far credere che quella differenza conti.
            setTint(hsv.g === 0 ? NEUTRAL_TINT : color3ToHex(BABYLON.Color3.FromHSV(hsv.r, hsv.g, 1)));

            sceneEditor?.setSceneColorCurvesHue?.(hsv.r);
            sceneEditor?.setSceneColorCurvesDensity?.(hsv.g * 100);
        },
        [sceneEditor]
    );

    const changeColorSaturation = useCallback(
        (value: number) => {
            setColorSaturation(value);
            sceneEditor?.setSceneColorCurvesSaturation?.(value);
        },
        [sceneEditor]
    );

    const changeBrightness = useCallback(
        (value: number) => {
            setBrightness(value);
            sceneEditor?.setSceneColorCurvesBrightness?.(value);
        },
        [sceneEditor]
    );

    // VIGNETTATURA — nella stessa `imageProcessingConfiguration` del color grading, quindi stessa
    // strada: tre campi nullable sul server, tre setter sul motore, e la rilettura al caricamento.
    // A schermo però è una card come il Glow, perché è quello che è per chi la usa: una cosa che si
    // accende e che ha due parametri dentro.
    const [vignette, setVignette] = useState<boolean>(generalConfig?.get?.('vignetteEnabled') ?? false);
    const [vignetteWeight, setVignetteWeight] = useState<number>(generalConfig?.get?.('vignetteWeight') ?? VIGNETTE_DEFAULT_WEIGHT);
    const [vignetteColor, setVignetteColor] = useState<string>(generalConfig?.get?.('vignetteColor') ?? VIGNETTE_DEFAULT_COLOR);

    const changeVignette = useCallback(
        (value: boolean) => {
            setVignette(value);
            sceneEditor?.setSceneVignetteEnabled?.(value);
        },
        [sceneEditor]
    );

    const changeVignetteWeight = useCallback(
        (value: number) => {
            setVignetteWeight(value);
            sceneEditor?.setSceneVignetteWeight?.(value);
        },
        [sceneEditor]
    );

    const changeVignetteColor = useCallback(
        (value: string) => {
            setVignetteColor(value);
            sceneEditor?.setSceneVignetteColor?.(value);
        },
        [sceneEditor]
    );

    // IL BLOCCO DEL GLOW NASCE QUANDO SI SCRIVE, E NASCE COMPLETO. `enabled` è obbligatorio
    // nell'input GraphQL, quindi non basta non creare il blocco vuoto: anche un glow con la sola
    // intensità è un payload che il server rifiuta. Chi scrive un parametro si porta dietro
    // `enabled`, che qui è sempre noto — è lo stato dell'interruttore che si vede.
    const writeGlowConfig = useCallback(
        (values: { enabled?: boolean; intensity?: number; blurSize?: number }) => {
            const config = zkScene?.getOrCreateGlowConfig?.();
            if (!config) return;

            config.set('enabled', values.enabled ?? config.get('enabled') ?? glow);
            if (values.intensity !== undefined) config.set('intensity', values.intensity);
            if (values.blurSize !== undefined) config.set('blurSize', values.blurSize);
        },
        [zkScene, glow]
    );

    // I TRE SETTER DEL GLOW SCRIVONO SOLO IL MOTORE. A differenza dei `setViewer*`, che il config
    // lo scrivono da sé, questi toccano il GlowManager e basta: da soli sarebbero una modifica che
    // si vede e che ricaricando non c'è più. Quindi si scrivono tutti e due — il motore per
    // vederlo subito, il config perché è ciò che si salva.
    const changeGlow = useCallback(
        (value: boolean) => {
            setGlow(value);
            sceneEditor?.setGlowEnabled?.(value);
            writeGlowConfig({ enabled: value });
        },
        [sceneEditor, writeGlowConfig]
    );

    const changeGlowIntensity = useCallback(
        (value: number) => {
            setGlowIntensity(value);
            sceneEditor?.setGlowIntensity?.(value);
            writeGlowConfig({ intensity: value });
        },
        [sceneEditor, writeGlowConfig]
    );

    const changeGlowBlur = useCallback(
        (value: number) => {
            setGlowBlur(value);
            sceneEditor?.setGlowBlurSize?.(value);
            writeGlowConfig({ blurSize: value });
        },
        [sceneEditor, writeGlowConfig]
    );

    return (
        <InspectorShell
            kind='effects'
            name={view === 'color' ? T._('Color correction', 'Admin') : T._('Post-process', 'Admin')}
            icon={view === 'color' ? <ColorCorrectionIcon /> : <PostProcessIcon />}
        >
            {view === 'color' && (
                <>
                    <PropertiesPanelAccordion header={T._('Tone mapping', 'Admin')}>
                        <PropertiesPanelSelectProperty
                            id='effects-tonemapping-mode'
                            label={T._('Mode', 'Admin')}
                            options={TONE_MAPPING_OPTIONS}
                            value={TONE_MAPPING_OPTIONS.find((option) => option.value === toneMappingMode) ?? TONE_MAPPING_OPTIONS[0]}
                            onChange={(option) => changeToneMapping(Number(option?.value ?? -1))}
                        />
                        <PropertiesPanelRangeProperty
                            id='effects-exposure'
                            label={T._('Exposure', 'Admin')}
                            value={exposure}
                            min={0}
                            max={4}
                            step={0.01}
                            onChange={changeExposure}
                        />
                    </PropertiesPanelAccordion>

                    <PropertiesPanelAccordion header={T._('Color grading', 'Admin')}>
                        {/* Gli intervalli sono quelli veri del motore, -100…+100, non quelli del mockup. */}
                        <PropertiesPanelColorProperty id='effects-tint' label={T._('Tint', 'Admin')} value={tint} onChange={changeTint} />
                        <PropertiesPanelRangeProperty
                            id='effects-saturation'
                            label={T._('Saturation', 'Admin')}
                            value={colorSaturation}
                            min={-100}
                            max={100}
                            step={1}
                            onChange={changeColorSaturation}
                        />
                        <PropertiesPanelRangeProperty
                            id='effects-brightness'
                            label={T._('Brightness', 'Admin')}
                            value={brightness}
                            min={-100}
                            max={100}
                            step={1}
                            onChange={changeBrightness}
                        />
                        <PropertiesPanelRangeProperty
                            id='effects-contrast'
                            label={T._('Contrast', 'Admin')}
                            value={contrast}
                            min={0}
                            max={4}
                            step={0.01}
                            onChange={changeContrast}
                        />
                    </PropertiesPanelAccordion>
                </>
            )}

            {view === 'postprocess' && (
                <>
                    {/* DUE DEI SEI ESISTONO DAVVERO, e hanno la forma che il mockup dà a tutti: una card
                    con l'interruttore in testata e dentro i suoi parametri. Si aprono da sé quando le
                    accendi — si accende una funzione per usarla.

                    Il glow e la vignettatura stanno insieme qui perché per chi le usa sono la stessa
                    cosa — qualcosa che si accende sull'immagine — ma sotto sono lontanissime: il glow
                    è un GlowLayer con un suo blocco nel config, la vignettatura tre campi della
                    `imageProcessingConfiguration` che non costano un passaggio. */}
                    <PropertiesPanelAccordion
                        header={T._('Glow', 'Admin')}
                        openWhen={glow}
                        trailing={<PropertiesPanelSwitch id='effects-glow' ariaLabel={T._('Glow', 'Admin')} checked={glow} onChange={changeGlow} />}
                    >
                        <PropertiesPanelRangeProperty
                            id='effects-glow-intensity'
                            label={T._('Intensity', 'Admin')}
                            value={glowIntensity}
                            min={0}
                            max={2}
                            step={0.05}
                            onChange={changeGlowIntensity}
                            disabled={!glow}
                        />
                        <PropertiesPanelRangeProperty
                            id='effects-glow-blur'
                            label={T._('Blur size', 'Admin')}
                            value={glowBlur}
                            min={0}
                            max={64}
                            step={1}
                            onChange={changeGlowBlur}
                            disabled={!glow}
                        />
                    </PropertiesPanelAccordion>

                    <PropertiesPanelAccordion
                        header={T._('Vignette', 'Admin')}
                        openWhen={vignette}
                        trailing={
                            <PropertiesPanelSwitch id='effects-vignette' ariaLabel={T._('Vignette', 'Admin')} checked={vignette} onChange={changeVignette} />
                        }
                    >
                        {/* IL MASSIMO È CINQUE, e non è un numero tondo scelto a caso: Babylon fa
                        `power = -2 * peso`, e oltre quel punto i bordi sono già neri e muovere il
                        cursore non cambia più niente. Un cursore che continua dove non succede nulla
                        insegna che il comando non risponde. */}
                        <PropertiesPanelRangeProperty
                            id='effects-vignette-weight'
                            label={T._('Amount', 'Admin')}
                            value={vignetteWeight}
                            min={0}
                            max={5}
                            step={0.1}
                            onChange={changeVignetteWeight}
                            disabled={!vignette}
                        />
                        {/* Il colore MOLTIPLICA l'immagine: nero scurisce i bordi, bianco non fa niente. */}
                        <PropertiesPanelColorProperty
                            id='effects-vignette-color'
                            label={T._('Color', 'Admin')}
                            value={vignetteColor}
                            onChange={changeVignetteColor}
                            disabled={!vignette}
                        />
                    </PropertiesPanelAccordion>

                    {/* NEL MOCKUP OGNI FILTRO È UNA CARD CHE SI APRE, con il suo interruttore in testata e
                    dentro i suoi parametri. Qui i parametri non esistono — nessuno dei sei ha un metodo
                    sul motore — e una card che si apre sul vuoto promette qualcosa che non c'è. Resta il
                    controllo giusto, l'interruttore, spento e non toccabile: dice che il filtro è
                    previsto e che oggi non si accende — quando `SHOW_NOT_YET_AVAILABLE` lo lascia
                    disegnare. Quando il motore arriverà, ognuno di questi diventa una card e i
                    parametri ci entrano dentro. */}
                    {SHOW_NOT_YET_AVAILABLE &&
                        FILTERS.map((filter) => (
                            <PropertiesPanelSwitchProperty
                                key={filter}
                                id={`effects-filter-${filter.toLowerCase().replace(/\s+/g, '-')}`}
                                label={T._(filter, 'Admin')}
                                caption={T._('Not available yet', 'Admin')}
                                checked={false}
                                disabled
                            />
                        ))}
                </>
            )}
        </InspectorShell>
    );
};

export default EffectsEditor;
